import type { ModelProps, ModelRegistry } from './types';

export function BookshelfModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Frame */}
      <mesh position={[0, 0.75, 0]} castShadow receiveShadow>
        <boxGeometry args={[1, 1.5, 0.3]} />
        <meshStandardMaterial
          color={world === 'real' ? '#5C4033' : '#4a3a5a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Shelves */}
      {[0.3, 0.75, 1.2].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} castShadow>
          <boxGeometry args={[0.9, 0.05, 0.25]} />
          <meshStandardMaterial color={world === 'real' ? '#6B4423' : '#5a4a6a'} />
        </mesh>
      ))}
      {/* Books */}
      {[0.5, 0.95].map((y, row) => (
        [-0.3, -0.1, 0.1, 0.3].map((x, i) => (
          <mesh key={`${row}-${i}`} position={[x, y, 0]} castShadow>
            <boxGeometry args={[0.12, 0.2, 0.18]} />
            <meshStandardMaterial
              color={['#8B0000', '#00008B', '#006400', '#4B0082'][i]}
              roughness={0.7}
            />
          </mesh>
        ))
      ))}
    </group>
  );
}

export function TableModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Top */}
      <mesh position={[0, 0.7, 0]} castShadow receiveShadow>
        <boxGeometry args={[1, 0.08, 0.6]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B7355' : '#6a5a7a'}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Legs */}
      {[[-0.4, -0.25], [-0.4, 0.25], [0.4, -0.25], [0.4, 0.25]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.33, z]} castShadow>
          <boxGeometry args={[0.08, 0.66, 0.08]} />
          <meshStandardMaterial color={world === 'real' ? '#6B4423' : '#5a4a6a'} />
        </mesh>
      ))}
    </group>
  );
}

export function ChairModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Seat */}
      <mesh position={[0, 0.35, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.4, 0.06, 0.4]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B7355' : '#6a5a7a'}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Back */}
      <mesh position={[0, 0.6, -0.17]} castShadow>
        <boxGeometry args={[0.4, 0.5, 0.06]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B7355' : '#6a5a7a'}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Legs */}
      {[[-0.15, -0.15], [-0.15, 0.15], [0.15, -0.15], [0.15, 0.15]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.16, z]} castShadow>
          <boxGeometry args={[0.05, 0.32, 0.05]} />
          <meshStandardMaterial color={world === 'real' ? '#6B4423' : '#5a4a6a'} />
        </mesh>
      ))}
    </group>
  );
}

export function AltarModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.2, 0.3, 0.8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#696969' : '#5a5a7a'}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Top */}
      <mesh position={[0, 0.4, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.4, 0.15, 1]} />
        <meshStandardMaterial
          color={world === 'real' ? '#808080' : '#6a6a8a'}
          roughness={0.5}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Candles on altar */}
      {[[-0.5, 0], [0.5, 0]].map(([x, z], i) => (
        <group key={i} position={[x, 0.5, z]}>
          <mesh position={[0, 0.1, 0]}>
            <cylinderGeometry args={[0.04, 0.05, 0.2, 8]} />
            <meshStandardMaterial color={world === 'real' ? '#FFF8DC' : '#E6E6FA'} />
          </mesh>
          <mesh position={[0, 0.25, 0]}>
            <coneGeometry args={[0.02, 0.06, 6]} />
            <meshStandardMaterial
              color={world === 'real' ? '#FFA500' : '#DA70D6'}
              emissive={world === 'real' ? '#FF6600' : '#BA55D3'}
              emissiveIntensity={1.5}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

export function GraveModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Stone */}
      <mesh position={[0, 0.4, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.5, 0.8, 0.15]} />
        <meshStandardMaterial
          color={world === 'real' ? '#696969' : '#5a5a7a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Top curve */}
      <mesh position={[0, 0.85, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.25, 0.15, 12, 1, false, 0, Math.PI]} />
        <meshStandardMaterial
          color={world === 'real' ? '#696969' : '#5a5a7a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Ground mound */}
      <mesh position={[0, 0.05, 0.3]} rotation={[-0.2, 0, 0]}>
        <boxGeometry args={[0.6, 0.1, 0.6]} />
        <meshStandardMaterial color={world === 'real' ? '#3d2817' : '#2a2a3a'} />
      </mesh>
    </group>
  );
}

export function CartModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.4, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.8, 0.3, 0.5]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B4513' : '#5a4a6a'}
          roughness={0.85}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Wheels */}
      {[[-0.35, 0.25], [-0.35, -0.25], [0.35, 0.25], [0.35, -0.25]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.15, z]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.15, 0.15, 0.05, 12]} />
          <meshStandardMaterial color={world === 'real' ? '#4a3a2a' : '#3a3a4a'} />
        </mesh>
      ))}
      {/* Handle */}
      <mesh position={[0.55, 0.35, 0]} rotation={[0, 0, -0.3]} castShadow>
        <boxGeometry args={[0.4, 0.06, 0.06]} />
        <meshStandardMaterial color={world === 'real' ? '#6B4423' : '#4a3a5a'} />
      </mesh>
    </group>
  );
}

export function LampModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Post */}
      <mesh position={[0, 0.75, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.06, 1.5, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#2a2a2a' : '#3a3a4a'}
          metalness={0.6}
          roughness={0.4}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Lamp head */}
      <mesh position={[0, 1.55, 0]} castShadow>
        <dodecahedronGeometry args={[0.15, 0]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FFD700' : '#9370DB'}
          emissive={world === 'real' ? '#FFA500' : '#8A2BE2'}
          emissiveIntensity={1}
          transparent
          opacity={0.8}
        />
      </mesh>
      <pointLight
        position={[0, 1.55, 0]}
        color={world === 'real' ? '#FFD700' : '#9370DB'}
        intensity={4}
        distance={6}
      />
    </group>
  );
}

export function DebrisModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {[
        [0, 0.08, 0, 0.2],
        [0.15, 0.05, 0.1, 0.12],
        [-0.12, 0.06, -0.08, 0.15],
        [0.08, 0.04, -0.15, 0.1],
        [-0.18, 0.05, 0.12, 0.13],
      ].map(([x, y, z, size], i) => (
        <mesh
          key={i}
          position={[x, y, z]}
          rotation={[Math.random(), Math.random(), Math.random()]}
          castShadow
        >
          <dodecahedronGeometry args={[size, 0]} />
          <meshStandardMaterial
            color={world === 'real' ? '#808080' : '#6a6a8a'}
            roughness={0.9}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
    </group>
  );
}

export function ColumnModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.1, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.35, 0.4, 0.2, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#d4c4a8' : '#9a8a9a'}
          roughness={0.6}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Shaft */}
      <mesh position={[0, 0.6, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.25, 0.3, 0.8, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#e8dcc8' : '#a89aaa'}
          roughness={0.5}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Broken top */}
      <mesh position={[0.05, 1.05, 0]} rotation={[0.1, 0, 0.15]} castShadow>
        <cylinderGeometry args={[0.2, 0.25, 0.15, 8, 1]} />
        <meshStandardMaterial
          color={world === 'real' ? '#e8dcc8' : '#a89aaa'}
          roughness={0.6}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function MirrorModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Frame */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <boxGeometry args={[0.6, 1, 0.08]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B7355' : '#6a5a7a'}
          roughness={0.6}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Mirror surface */}
      <mesh position={[0, 0.8, 0.03]}>
        <planeGeometry args={[0.5, 0.9]} />
        <meshStandardMaterial
          color={world === 'real' ? '#E0FFFF' : '#DDA0DD'}
          metalness={0.9}
          roughness={0.1}
        />
      </mesh>
      {/* Stand */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <boxGeometry args={[0.4, 0.3, 0.3]} />
        <meshStandardMaterial
          color={world === 'real' ? '#6B4423' : '#5a4a6a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function AnvilModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.5, 0.3, 0.3]} />
        <meshStandardMaterial
          color={world === 'real' ? '#2a2a2a' : '#3a3a4a'}
          metalness={0.8}
          roughness={0.4}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Top */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <boxGeometry args={[0.6, 0.2, 0.25]} />
        <meshStandardMaterial
          color={world === 'real' ? '#3a3a3a' : '#4a4a5a'}
          metalness={0.9}
          roughness={0.3}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Horn */}
      <mesh position={[0.4, 0.4, 0]} rotation={[0, 0, -0.2]} castShadow>
        <coneGeometry args={[0.08, 0.25, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#3a3a3a' : '#4a4a5a'}
          metalness={0.9}
          roughness={0.3}
        />
      </mesh>
    </group>
  );
}

export function CauldronModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Pot */}
      <mesh position={[0, 0.3, 0]} castShadow receiveShadow>
        <sphereGeometry args={[0.35, 12, 12, 0, Math.PI * 2, 0, Math.PI / 1.3]} />
        <meshStandardMaterial
          color={world === 'real' ? '#1a1a1a' : '#2a2a3a'}
          metalness={0.7}
          roughness={0.5}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Rim */}
      <mesh position={[0, 0.55, 0]}>
        <torusGeometry args={[0.32, 0.05, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#2a2a2a' : '#3a3a4a'}
          metalness={0.8}
          roughness={0.4}
        />
      </mesh>
      {/* Liquid */}
      <mesh position={[0, 0.5, 0]}>
        <cylinderGeometry args={[0.28, 0.28, 0.05, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#00FF00' : '#9400D3'}
          emissive={world === 'real' ? '#00AA00' : '#7B00B3'}
          emissiveIntensity={0.5}
          transparent
          opacity={0.8}
        />
      </mesh>
      {/* Legs */}
      {[0, 1, 2].map((i) => (
        <mesh
          key={i}
          position={[
            Math.cos((i / 3) * Math.PI * 2) * 0.3,
            0.1,
            Math.sin((i / 3) * Math.PI * 2) * 0.3
          ]}
          castShadow
        >
          <cylinderGeometry args={[0.04, 0.03, 0.2, 6]} />
          <meshStandardMaterial
            color={world === 'real' ? '#1a1a1a' : '#2a2a3a'}
            metalness={0.7}
          />
        </mesh>
      ))}
    </group>
  );
}

export function BannerModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Pole */}
      <mesh position={[0, 0.75, 0]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 1.5, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B4513' : '#5a4a6a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Banner cloth */}
      <mesh position={[0.25, 0.9, 0]} castShadow>
        <boxGeometry args={[0.5, 0.8, 0.02]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B0000' : '#4B0082'}
          roughness={0.9}
          side={2}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Top decoration */}
      <mesh position={[0, 1.55, 0]}>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FFD700' : '#9370DB'}
          metalness={0.7}
          roughness={0.3}
        />
      </mesh>
    </group>
  );
}

export function WeaponRackModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Frame */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <boxGeometry args={[0.8, 1.2, 0.1]} />
        <meshStandardMaterial
          color={world === 'real' ? '#5C4033' : '#4a3a5a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Sword 1 */}
      <mesh position={[-0.2, 0.7, 0.08]} rotation={[0, 0, 0.2]} castShadow>
        <boxGeometry args={[0.08, 0.7, 0.02]} />
        <meshStandardMaterial color={world === 'real' ? '#C0C0C0' : '#9090B0'} metalness={0.8} />
      </mesh>
      {/* Sword 2 */}
      <mesh position={[0.2, 0.65, 0.08]} rotation={[0, 0, -0.15]} castShadow>
        <boxGeometry args={[0.06, 0.6, 0.02]} />
        <meshStandardMaterial color={world === 'real' ? '#B8860B' : '#8B7355'} metalness={0.7} />
      </mesh>
      {/* Shield */}
      <mesh position={[0, 0.3, 0.1]} castShadow>
        <circleGeometry args={[0.2, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B0000' : '#4B0082'}
          metalness={0.3}
          roughness={0.6}
        />
      </mesh>
    </group>
  );
}

export const decorativeFurnitureModels: ModelRegistry = {
  bookshelf: BookshelfModel,
  table: TableModel,
  chair: ChairModel,
  altar: AltarModel,
  grave: GraveModel,
  cart: CartModel,
  lamp: LampModel,
  debris: DebrisModel,
  column: ColumnModel,
  mirror: MirrorModel,
  anvil: AnvilModel,
  cauldron: CauldronModel,
  banner: BannerModel,
  weapon_rack: WeaponRackModel,
};
